import { useState, VFC, memo, useCallback } from "react";

import LikeShare from "components/articles/likeShare";

import { useHeadingsData } from "hooks/articles/useHeadingsData";
import { useIntersectionObserver } from "hooks/articles/useIntersectionObserver";

import { CurrentUser, Heading } from "lib/type";

import { IoBookmarks } from "react-icons/io5";
import { BsFillCaretDownFill } from "react-icons/bs";

type HeadingsProps = {
  headings: Heading[];
  activeId: string;
  toggleToc: () => void;
};

const Headings: VFC<HeadingsProps> = ({ headings, activeId, toggleToc }) => {
  const thirdHeadings = (child: { id: any; title: string }) => {
    const { id, title } = child;
    return (
      <li key={id}>
        <a
          className={`relative bg-opacity-70 font-normal ${
            id === activeId ? "font-bold opacity-100" : ""
          }`}
          href={`#${id}`}
          onClick={toggleToc}
        >
          {id === activeId && (
            <BsFillCaretDownFill className="absolute top-1/2 -left-4 -translate-y-1/2 -rotate-90 transform" />
          )}
          {title}
        </a>
      </li>
    );
  };

  const secondHeadings = headings.map((heading) => {
    const { id, title, items } = heading;
    return (
      <li key={id}>
        <a
          className={`relative bg-opacity-70 ${
            id === activeId ? "font-bold opacity-100" : ""
          }`}
          href={`#${id}`}
          onClick={toggleToc}
        >
          {id === activeId && (
            <BsFillCaretDownFill className="absolute top-1/2 -left-4 -translate-y-1/2 -rotate-90 transform" />
          )}
          {title}
        </a>
        {items.length > 0 && (
          <ul className="mt-2 space-y-2 pl-4">
            {items.map((child) => thirdHeadings(child))}
          </ul>
        )}
      </li>
    );
  });

  return (
    <ul className="ml-auto mb-4 table space-y-2 pl-4 lg:ml-0">
      {secondHeadings}
    </ul>
  );
};

type TocProps = {
  isAuthChecking: boolean;
  user: CurrentUser;
  title: string;
  url: string;
  isCurrentLiked: boolean;
  currentLikeCount: number;
  changeLike: () => void;
  signin: () => void;
};
const Toc: VFC<TocProps> = ({
  isAuthChecking,
  user,
  title,
  url,
  isCurrentLiked,
  currentLikeCount,
  changeLike,
  signin,
}) => {
  const [activeId, setActiveId] = useState("");
  const { nestedHeadings } = useHeadingsData();
  useIntersectionObserver(setActiveId);

  const [isOpenToc, setIsOpenToc] = useState(false);
  const toggleToc = useCallback(
    () => setIsOpenToc((isOpenToc) => !isOpenToc),
    []
  );

  return (
    <>
      <button
        onClick={toggleToc}
        className="flex w-full items-center justify-end pb-2 font-bold lg:pointer-events-none lg:ml-0 lg:cursor-default lg:justify-start"
      >
        <IoBookmarks className="mr-1" />
        目次
        <BsFillCaretDownFill
          className={`ml-4 ${
            isOpenToc ? "rotate-180 transform" : ""
          } lg:hidden`}
        />
      </button>
      <div
        className={`transition ${
          isOpenToc
            ? "pointer-events-auto h-full"
            : "pointer-events-none h-0 pt-0"
        } overflow-y-auto lg:pointer-events-auto lg:h-full lg:pt-2`}
      >
        <Headings {...{ activeId, toggleToc }} headings={nestedHeadings} />
        <LikeShare
          {...{
            isAuthChecking,
            user,
            title,
            url,
            isCurrentLiked,
            currentLikeCount,
            changeLike,
            signin,
          }}
        />
      </div>
    </>
  );
};

export default memo(Toc);
